import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Package, AlertTriangle, MapPin, ShoppingCart, Loader } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DashboardService, { DashboardStats } from '../services/dashboardService';

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await DashboardService.getStats();
      setStats(data);
    } catch (err: any) {
      console.error('Erro ao carregar dashboard:', err);
      setError(err.message || 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      await DashboardService.syncAll();
      alert('Sincronização iniciada com sucesso! 🎉');
      // Recarregar estatísticas após 2 segundos
      setTimeout(() => {
        loadDashboardStats();
      }, 2000);
    } catch (err: any) {
      console.error('Erro ao sincronizar:', err);
      alert(err.message || 'Erro ao iniciar sincronização');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="text-center">
          <Loader className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-dark-400">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-red-400 mb-2">Erro ao carregar dashboard</h3>
          <p className="text-red-300 mb-4">{error}</p>
          <button
            onClick={loadDashboardStats}
            className="px-6 py-2 bg-red-500 hover:bg-red-600 text-dark-100 rounded-lg font-medium transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-8">
        <div className="bg-dark-800 rounded-lg p-6 text-center">
          <p className="text-dark-400">Nenhum dado disponível</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-dark-100 mb-2">Dashboard</h1>
          <p className="text-dark-400">Visão geral do seu hub de marketplaces</p>
        </div>
        <button
          onClick={handleSyncAll}
          disabled={syncing}
          className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-700 disabled:text-dark-500 text-dark-100 rounded-lg font-medium transition-all shadow-lg shadow-primary-500/20"
        >
          {syncing ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Sincronizando...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Sincronizar Tudo
            </>
          )}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-dark-800 p-6 rounded-xl border border-dark-700 hover:border-dark-600 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-primary-500/10 rounded-lg">
              <Package className="w-6 h-6 text-primary-500" />
            </div>
          </div>
          <h3 className="text-dark-400 text-sm mb-1">Total de Produtos</h3>
          <p className="text-3xl font-bold text-dark-100">{stats.totalProducts}</p>
          <p className="text-sm text-dark-500 mt-2">{stats.activeProducts} ativos</p>
        </div>

        <div className="bg-dark-800 p-6 rounded-xl border border-dark-700 hover:border-dark-600 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-500/10 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          </div>
          <h3 className="text-dark-400 text-sm mb-1">Com Erros</h3>
          <p className="text-3xl font-bold text-dark-100">{stats.productsWithErrors}</p>
          <p className="text-sm text-dark-500 mt-2">Requer atenção</p>
        </div>

        <div className="bg-dark-800 p-6 rounded-xl border border-dark-700 hover:border-dark-600 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <MapPin className="w-6 h-6 text-purple-400" />
            </div>
          </div>
          <h3 className="text-dark-400 text-sm mb-1">Mapeamentos Pendentes</h3>
          <p className="text-3xl font-bold text-dark-100">{stats.pendingMappings}</p>
          <p className="text-sm text-dark-500 mt-2">Categorias</p>
        </div>

        <div className="bg-dark-800 p-6 rounded-xl border border-dark-700 hover:border-dark-600 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-emerald-500/10 rounded-lg">
              <ShoppingCart className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
          <h3 className="text-dark-400 text-sm mb-1">Pedidos Hoje</h3>
          <p className="text-3xl font-bold text-dark-100">{stats.ordersToday}</p>
          <p className="text-sm text-dark-500 mt-2">{stats.totalOrders} total</p>
        </div>
      </div>

      {/* Revenue */}
      <div className="bg-dark-800 p-6 rounded-xl border border-dark-700">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-dark-100">Receita</h3>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            stats.revenueChange >= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
          }`}>
            {stats.revenueChange >= 0 ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <TrendingDown className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {stats.revenueChange >= 0 ? '+' : ''}{stats.revenueChange.toFixed(1)}%
            </span>
          </div>
        </div>
        <p className="text-4xl font-bold text-dark-100 mb-2">
          R$ {stats.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
        <p className="text-dark-500 text-sm">
          Últimos 30 dias · vs. 30 dias anteriores
          {typeof (stats as any).revenuePrevious === 'number' && (
            <> (R$ {(stats as any).revenuePrevious.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</>
          )}
        </p>
      </div>

      {/* Channel Distribution */}
      <div className="bg-dark-800 p-6 rounded-xl border border-dark-700">
        <h3 className="text-xl font-bold text-dark-100 mb-6">Distribuição por Canal</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={stats.channelDistribution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#D6CFFA" />
            <XAxis dataKey="marketplaceName" stroke="#6B5FA8" tick={{ fill: '#6B5FA8', fontSize: 12 }} />
            <YAxis stroke="#6B5FA8" tick={{ fill: '#6B5FA8', fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #D6CFFA',
                borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(91,63,224,0.12)',
              }}
              labelStyle={{ color: '#1C1545', fontWeight: 600 }}
              itemStyle={{ color: '#5B3FE0' }}
            />
            <Bar dataKey="productCount" fill="#7B5CFA" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Errors */}
      {stats.recentErrors && stats.recentErrors.length > 0 && (
        <div className="bg-dark-800 p-6 rounded-xl border border-dark-700">
          <h3 className="text-xl font-bold text-dark-100 mb-6">Erros Recentes</h3>
          <div className="space-y-3">
            {stats.recentErrors.map((error) => (
              <div
                key={error.id}
                className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-400 mb-1">{error.code}</p>
                    <p className="text-sm text-dark-300">{error.message}</p>
                    <p className="text-xs text-dark-500 mt-1">
                      {new Date(error.timestamp).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
